import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDocumentLang } from '@/hooks/useDocumentLang';
import { useJsonLd, useSeoMeta } from '@/hooks/useSeoMeta';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/components/ui/cn';
import { countriesQuery, type Country, type CountryCode } from '@/data';
import { buildWikiUrl, type CountryCode as WikiCountryCode } from '@/features/wiki/architecture';
import { useWikiHreflang } from '@/features/wiki/seo';
import { stripLangPrefix } from '@/i18n/paths';
import { buildGuideUrl } from '@/features/guide';
import { checklistPath } from '@/features/next-steps/slugs';
import {
  useCostOfLiving,
  getHouseholdTotal,
  DEFAULT_DISPLAY_BUCKET,
  type CostOfLivingFile,
} from '@/features/quiz/cost-of-living';
import {
  CostTaxRow,
  CountryScoreCard,
  DEFAULT_WEIGHTS,
  useQuizStore,
  type CountryMatchResult,
} from '@/features/quiz';
import { PropertyTaxDetail } from '@/features/property-tax';
import { regionLabel } from '@/lib/region-label';
import {
  CarOwnershipDetail,
  useCarOwnership,
  getCarOwnership,
  getCarTierColor,
} from '@/features/car-ownership';

/* ────────────────────────────────────────────────────────────────── */
/*  Filters & sorting                                                  */
/* ────────────────────────────────────────────────────────────────── */

/** Canonical host — must match the origin build-sitemap.mjs advertises. */
const PROD_ORIGIN = 'https://lodestar.place';

const REGION_FILTERS = [
  { key: 'all', test: () => true },
  { key: 'europe', test: (c: Country) => c.region === 'Europe' },
  { key: 'asia', test: (c: Country) => c.region === 'Asia' || c.region === 'Southeast Asia' },
  { key: 'latam', test: (c: Country) => c.region === 'Latin America' || c.region === 'South America' },
  { key: 'middleeast', test: (c: Country) => c.region === 'Middle East' },
  { key: 'africa', test: (c: Country) => c.region === 'Africa' || c.region === 'Indian Ocean' || c.region === 'Caribbean' },
] as const;

type RegionKey = typeof REGION_FILTERS[number]['key'];

const ALL_SORT_KEYS = ['match', 'tier', 'name', 'cost', 'tax'] as const;
type SortKey = typeof ALL_SORT_KEYS[number];

const TIER_RANK: Record<string, number> = { T0: 0, T1: 1, T2: 2, T3: 3 };

const VIEW_KEYS = ['grid', 'list'] as const;
type ViewKey = typeof VIEW_KEYS[number];

// Visa path bucketing — maps free-text country.visa_programs strings onto
// three practical buckets a user reasons about. Each visa program may match
// multiple buckets (e.g. "Premium Visa (0% tax for nomads)" → nomad +
// investment-ish), so we keep this string-pattern based and tolerant.
const VISA_BUCKETS = ['any', 'nomad', 'freelance', 'investment'] as const;
type VisaBucket = typeof VISA_BUCKETS[number];

function matchesVisaBucket(programs: string[], bucket: VisaBucket): boolean {
  if (bucket === 'any') return true;
  const text = programs.join(' | ').toLowerCase();
  switch (bucket) {
    case 'nomad':
      return /\b(nomad|remote|dtv|rantau|one pass)\b/.test(text);
    case 'freelance':
      return /\b(freelance|self-employ|self employ|entrepreneur|d7|rentista|pink slip|critical skills|temporary resident|residency|self-employment|e-residency|company-based)\b/.test(text);
    case 'investment':
      return /\b(golden|investment|friendly nations|entrepass|company formation|long-term|ltr|mm2h)\b/.test(text);
    default:
      return false;
  }
}

interface CountryEntry {
  country: Country;
  match?: CountryMatchResult;
  excluded: boolean;
  unscored: boolean;
}

function buildEntries(
  countries: Country[],
  topMatches: CountryMatchResult[] | undefined,
  excluded: CountryMatchResult[] | undefined,
  unscored: CountryCode[] | undefined,
): CountryEntry[] {
  const topByCode = new Map(topMatches?.map((m) => [m.countryCode, m]) ?? []);
  const exclByCode = new Map(excluded?.map((m) => [m.countryCode, m]) ?? []);
  const unscoredSet = new Set<CountryCode>(unscored ?? []);

  return countries.map((c) => {
    if (topByCode.has(c.code)) {
      return { country: c, match: topByCode.get(c.code), excluded: false, unscored: false };
    }
    if (exclByCode.has(c.code)) {
      return { country: c, match: exclByCode.get(c.code), excluded: true, unscored: false };
    }
    return { country: c, excluded: false, unscored: unscoredSet.has(c.code) };
  });
}

function sortEntries(
  entries: CountryEntry[],
  sortKey: SortKey,
  lang: 'en' | 'ru',
  costFile: CostOfLivingFile | undefined,
): CountryEntry[] {
  const collator = new Intl.Collator(lang === 'ru' ? 'ru' : 'en');
  return [...entries].sort((a, b) => {
    // Always push excluded after eligible and unscored last.
    const groupA = a.excluded ? 2 : a.unscored ? 1 : 0;
    const groupB = b.excluded ? 2 : b.unscored ? 1 : 0;
    if (groupA !== groupB) return groupA - groupB;

    switch (sortKey) {
      case 'match':
        return (b.match?.score ?? 0) - (a.match?.score ?? 0);
      case 'tier':
        return (TIER_RANK[a.country.tier] ?? 9) - (TIER_RANK[b.country.tier] ?? 9);
      case 'name': {
        const aName = lang === 'ru' ? a.country.name_ru : a.country.name_en;
        const bName = lang === 'ru' ? b.country.name_ru : b.country.name_en;
        return collator.compare(aName, bName);
      }
      case 'cost': {
        // Sorts on the single-person basket excluding schooling, which is the
        // only figure comparable across all 31 countries: childcare_education
        // measures local provision in most and international-school fees in
        // six, so a total that includes it ranks Bangkok above Vienna while
        // Bangkok's rent is half. See docs/data/COST_OF_LIVING_SCHEMA.md.
        const aV = getHouseholdTotal(costFile, a.country.code, DEFAULT_DISPLAY_BUCKET)
          ?? Number.POSITIVE_INFINITY;
        const bV = getHouseholdTotal(costFile, b.country.code, DEFAULT_DISPLAY_BUCKET)
          ?? Number.POSITIVE_INFINITY;
        return aV - bV;
      }
      case 'tax': {
        const aV = a.country.metrics?.tax.pit_top_rate_pct ?? Number.POSITIVE_INFINITY;
        const bV = b.country.metrics?.tax.pit_top_rate_pct ?? Number.POSITIVE_INFINITY;
        return aV - bV;
      }
      default:
        return 0;
    }
  });
}

/* ────────────────────────────────────────────────────────────────── */
/*  Skeleton / loading                                                 */
/* ────────────────────────────────────────────────────────────────── */

function CountryCardSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="flex animate-pulse flex-col gap-3 rounded-lg border border-ink-100 bg-paper-2 p-5"
    >
      <div className="flex items-start justify-between">
        <div className="h-8 w-8 rounded bg-ink-100" />
        <div className="h-8 w-14 rounded bg-ink-100" />
      </div>
      <div className="h-5 w-32 rounded bg-ink-100" />
      <div className="h-3 w-24 rounded bg-ink-100" />
      <div className="mt-2 h-2 w-full rounded bg-ink-100" />
      <div className="mt-4 h-4 w-20 rounded bg-ink-100" />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  Cards: neutral (unscored), excluded                                */
/* ────────────────────────────────────────────────────────────────── */

interface NeutralCountryCardProps {
  country: Country;
  lang: 'en' | 'ru';
}

function NeutralCountryCard({ country, lang }: NeutralCountryCardProps) {
  const { t } = useTranslation();
  const name = lang === 'ru' ? country.name_ru : country.name_en;
  const hasMap = country.data_available && !!country.map_url;
  const hubUrl = buildWikiUrl({ cc: country.code as WikiCountryCode, lang });
  const { data: carFile } = useCarOwnership();
  const carEntry = getCarOwnership(carFile, country.code);

  return (
    <article
      className="flex flex-col rounded-lg border border-ink-100 bg-paper-2 p-5 shadow-sm transition-shadow hover:shadow-md"
      data-testid={`country-card-${country.code}`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-3xl leading-none" aria-hidden="true">
          {country.flag_emoji}
        </span>
        <Tooltip
          content={t(`countries.tier.tooltip${country.tier}`, {
            defaultValue: country.tier,
          })}
        >
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
            {t(`countries.tier.${country.tier}`, { defaultValue: country.tier })}
          </span>
        </Tooltip>
      </div>

      <h3 className="mt-3 font-display text-xl font-medium italic tracking-tight text-ink-900">
        {name}
      </h3>
      <p className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-ink-500">
        {country.code} · {regionLabel(country.region, t)}
      </p>

      <CostTaxRow country={country} t={t} />

      {country.visa_programs.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {country.visa_programs.slice(0, 3).map((p) => (
            <span
              key={p}
              className="rounded-full border border-ink-100 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-500"
            >
              {p}
            </span>
          ))}
          {country.visa_programs.length > 3 && (
            <Tooltip content={country.visa_programs.slice(3).join(', ')}>
              <span className="rounded-full border border-ink-100 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-500">
                +{country.visa_programs.length - 3}
              </span>
            </Tooltip>
          )}
        </div>
      )}

      <details
        className="mt-4 rounded-md border border-ink-100 bg-paper open:bg-paper-3"
        data-testid={`country-owning-${country.code}`}
      >
        <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-ink-700 hover:text-ink-900">
          {t('propertyTax.summaryTitle', { defaultValue: 'Owning a home here' })}
          <span aria-hidden="true">▾</span>
        </summary>
        <div className="border-t border-ink-100 p-2">
          <PropertyTaxDetail countryCode={country.code} compact />
        </div>
      </details>

      {carEntry && (
        <details
          className="mt-2 rounded-md border border-ink-100 bg-paper open:bg-paper-3"
          data-testid={`country-car-${country.code}`}
        >
          <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-ink-700 hover:text-ink-900">
            <span className="flex items-center gap-1.5">
              🚗
              <span>{t('carOwnership.summaryTitle', { defaultValue: 'Car ownership' })}</span>
              {carEntry.confidence !== 'low' && carEntry.monthly_total_est_eur != null && (
                <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${getCarTierColor(carEntry.tier)}`}>
                  {t('carOwnership.chipMonthly', {
                    amount: carEntry.monthly_total_est_eur,
                    defaultValue: `~€${carEntry.monthly_total_est_eur}/mo`,
                  })}
                </span>
              )}
            </span>
            <span aria-hidden="true">▾</span>
          </summary>
          <div className="border-t border-ink-100 p-2">
            <CarOwnershipDetail entry={carEntry} countryCode={country.code} lang={lang} />
          </div>
        </details>
      )}

      <div className="mt-auto flex flex-wrap items-baseline gap-x-4 gap-y-1 pt-4">
        <Link
          to={hubUrl}
          className="font-mono text-xs font-medium uppercase tracking-wider text-terra-600 hover:underline"
        >
          {t('countries.openGuide')}
        </Link>
        {/* Moving checklist — universal across all roster countries. */}
        <Link
          to={checklistPath(country.code)}
          className="font-mono text-[11px] uppercase tracking-wider text-ink-500 hover:text-ink-900 hover:underline"
          data-testid={`country-card-cta-checklist-${country.code}`}
        >
          {t('countries.checklist', { defaultValue: 'Checklist →' })}
        </Link>
        {hasMap && (
          <Link
            to={country.map_url!}
            className="font-mono text-[11px] uppercase tracking-wider text-ink-500 hover:text-ink-900 hover:underline"
          >
            {t('countries.openMap')}
          </Link>
        )}
      </div>
    </article>
  );
}

interface ExcludedCountryCardProps {
  country: Country;
  match: CountryMatchResult;
  lang: 'en' | 'ru';
}

function ExcludedCountryCard({ country, match, lang }: ExcludedCountryCardProps) {
  const { t } = useTranslation();
  const name = lang === 'ru' ? country.name_ru : country.name_en;
  const reason = match.exclusionReasons?.[0];

  return (
    <article
      className="flex flex-col rounded-lg border border-ink-100 bg-paper-2/60 p-5 opacity-70"
      data-testid={`country-card-${country.code}`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-3xl leading-none grayscale" aria-hidden="true">
          {country.flag_emoji}
        </span>
        <span className="rounded-full border border-ochre-600/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ochre-600">
          {t('countries.excluded')}
        </span>
      </div>

      <h3 className="mt-3 font-display text-xl font-medium italic tracking-tight text-ink-700">
        {name}
      </h3>
      <p className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-ink-400">
        {country.code} · {regionLabel(country.region, t)}
      </p>

      {reason && (
        <p className="mt-3 font-mono text-[11px] text-ink-500">
          {t(`quiz.result.blocker.${reason}`, { defaultValue: reason })}
        </p>
      )}
    </article>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  Dense list-row (view=list)                                         */
/* ────────────────────────────────────────────────────────────────── */

interface CountryListRowProps {
  country: Country;
  match?: CountryMatchResult;
  excluded: boolean;
  lang: 'en' | 'ru';
}

function CountryListRow({ country, match, excluded, lang }: CountryListRowProps) {
  const { t } = useTranslation();
  const { data: costFile } = useCostOfLiving();
  // Single-person basket excluding schooling; the column header names the
  // household. The fabricated `metrics.cost.monthly_eur_family` this replaces
  // showed one "family of ~4" figure to every reader.
  const costOfLiving = getHouseholdTotal(costFile, country.code, DEFAULT_DISPLAY_BUCKET);
  const percent = match && !excluded ? Math.round(match.score * 100) : null;
  const name = lang === 'ru' ? country.name_ru : country.name_en;
  const hubUrl = buildWikiUrl({ cc: country.code as WikiCountryCode, lang });

  const checklistHref = checklistPath(country.code);

  return (
    <li
      data-testid={`country-row-${country.code}`}
      className={cn(
        'grid grid-cols-[28px_28px_1fr_64px_64px_72px_88px_88px] items-center gap-3 border-b border-ink-100 py-3 last:border-0 sm:gap-4',
        excluded && 'opacity-60',
      )}
    >
      <span
        className={cn('text-xl leading-none', excluded && 'grayscale')}
        aria-hidden="true"
      >
        {country.flag_emoji}
      </span>
      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-400">
        {country.code}
      </span>
      <span className="min-w-0">
        <span
          className={cn(
            'font-display text-base font-medium italic',
            excluded ? 'text-ink-500' : 'text-ink-900',
          )}
        >
          {name}
        </span>
        <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-ink-400">
          {regionLabel(country.region, t)}
        </span>
      </span>
      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
        {t(`countries.tier.${country.tier}`, { defaultValue: country.tier })}
      </span>
      <span className="text-right font-mono text-xs tabular-nums text-ink-700">
        {costOfLiving != null ? `€${LIST_MONEY.format(costOfLiving)}` : '—'}
      </span>
      <span className="text-right font-mono text-xs tabular-nums text-ink-700">
        {country.metrics?.tax.pit_top_rate_pct != null
          ? `${country.metrics.tax.pit_top_rate_pct}%`
          : '—'}
      </span>
      <span className="text-right">
        {excluded ? (
          <span className="font-mono text-[10px] uppercase tracking-wider text-ochre-600">
            {t('countries.excluded')}
          </span>
        ) : percent !== null ? (
          <Link
            to={hubUrl}
            className="font-mono text-sm font-semibold tabular-nums text-terra-600 hover:underline"
            aria-label={t('countries.openGuide')}
          >
            {percent}%
          </Link>
        ) : (
          <Link
            to={hubUrl}
            className="font-mono text-[11px] font-medium uppercase tracking-wider text-terra-600 hover:underline"
          >
            {t('countries.openGuide')}
          </Link>
        )}
      </span>
      {/* Moving-checklist column. Per-country checklist surface lives at
          /checklists/<slug>. Always linkable, including for excluded
          countries — users may still want the practical to-do list even
          if the country fell out on a hard filter. */}
      <span className="text-right">
        <Link
          to={checklistHref}
          className="font-mono text-[11px] uppercase tracking-wider text-ink-500 hover:text-ink-900"
          data-testid={`country-row-checklist-${country.code}`}
        >
          {t('countries.checklist', { defaultValue: 'Checklist →' })}
        </Link>
      </span>
    </li>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  Coming-soon strip                                                  */
/* ────────────────────────────────────────────────────────────────── */

interface ShippingStripProps {
  countries: Country[];
  lang: 'en' | 'ru';
}

// Approximate quarter mapping for T1 countries — adjust as roadmap firms up.
const SHIPPING_QUARTER: Record<string, { q: number; year: number }> = {
  AE: { q: 4, year: 2026 },
  PT: { q: 3, year: 2026 },
  ES: { q: 3, year: 2026 },
  CY: { q: 4, year: 2026 },
};

function ShippingStrip({ countries, lang }: ShippingStripProps) {
  const { t } = useTranslation();
  const t1 = countries.filter((c) => c.tier === 'T1');
  if (t1.length === 0) return null;

  return (
    <section
      data-testid="shipping-strip"
      className="mt-10 rounded-lg border border-ink-100 bg-paper-2/60 p-5 sm:p-6"
    >
      <h2 className="font-display text-xl font-medium italic tracking-tight text-ink-900">
        {t('countries.shipping.title')}
      </h2>
      <p className="mt-1 text-sm text-ink-500">{t('countries.shipping.subtitle')}</p>

      <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-3">
        {t1.map((c) => {
          const name = lang === 'ru' ? c.name_ru : c.name_en;
          const q = SHIPPING_QUARTER[c.code];
          return (
            <li key={c.code} className="flex items-baseline gap-2">
              <span className="text-lg leading-none" aria-hidden="true">
                {c.flag_emoji}
              </span>
              <span className="font-display text-base font-medium italic text-ink-900">
                {name}
              </span>
              {q && (
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-500">
                  {t('countries.shipping.quarter', { q: q.q, year: q.year })}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  Top-3 highlight                                                    */
/* ────────────────────────────────────────────────────────────────── */

interface TopThreeProps {
  matches: CountryMatchResult[];
  byCode: Map<string, Country>;
  lang: 'en' | 'ru';
  total: number;
}

function TopThreeHighlight({ matches, byCode, lang, total }: TopThreeProps) {
  const { t } = useTranslation();
  if (matches.length === 0) return null;

  const top = matches.slice(0, 3);

  return (
    <section className="mb-10" aria-labelledby="top-three-title">
      <h2
        id="top-three-title"
        className="mb-4 font-mono text-[11px] font-medium uppercase tracking-wider text-ink-500"
      >
        {t('countries.topThree')}
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {top.map((match, idx) => {
          const country = byCode.get(match.countryCode);
          if (!country) return null;
          return (
            <CountryScoreCard
              key={match.countryCode}
              match={match}
              country={country}
              lang={lang}
              weights={DEFAULT_WEIGHTS}
              rank={idx + 1}
              total={total}
              t={t}
            />
          );
        })}
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  Controls bar                                                       */
/* ────────────────────────────────────────────────────────────────── */

interface ControlsBarProps {
  region: RegionKey;
  onRegionChange: (r: RegionKey) => void;
  sortKey: SortKey;
  onSortChange: (s: SortKey) => void;
  availableSorts: readonly SortKey[];
  visaBucket: VisaBucket;
  onVisaChange: (v: VisaBucket) => void;
  view: ViewKey;
  onViewChange: (v: ViewKey) => void;
}

function ControlsBar({
  region,
  onRegionChange,
  sortKey,
  onSortChange,
  availableSorts,
  visaBucket,
  onVisaChange,
  view,
  onViewChange,
}: ControlsBarProps) {
  const { t } = useTranslation();
  return (
    <div className="mb-6 space-y-4">
      <div
        role="group"
        aria-label={t('countries.regionFilterLabel', { defaultValue: 'Region filter' })}
        className="flex flex-wrap gap-2"
      >
        {REGION_FILTERS.map((f) => {
          const active = region === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => onRegionChange(f.key)}
              data-testid={`region-${f.key}`}
              aria-pressed={active}
              className={cn(
                'rounded-full border px-3 py-1 font-mono text-xs uppercase tracking-wider transition-colors',
                active
                  ? 'border-terra-600 bg-terra-600 text-paper'
                  : 'border-ink-100 text-ink-500 hover:border-ink-300 hover:bg-paper-2',
              )}
            >
              {t(`countries.filter.${f.key}`)}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-ink-500">
          {t('countries.visa.label')}
          <select
            value={visaBucket}
            onChange={(e) => onVisaChange(e.target.value as VisaBucket)}
            data-testid="visa-select"
            className="rounded-md border border-ink-100 bg-paper px-2 py-1 font-mono text-xs uppercase tracking-wider text-ink-700 hover:border-ink-300 focus:border-terra-600 focus:outline-none focus:ring-2 focus:ring-terra-600/30"
          >
            {VISA_BUCKETS.map((v) => (
              <option key={v} value={v}>
                {t(`countries.visa.${v}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-ink-500">
          {t('countries.sort.label')}
          <select
            value={sortKey}
            onChange={(e) => onSortChange(e.target.value as SortKey)}
            data-testid="sort-select"
            className="rounded-md border border-ink-100 bg-paper px-2 py-1 font-mono text-xs uppercase tracking-wider text-ink-700 hover:border-ink-300 focus:border-terra-600 focus:outline-none focus:ring-2 focus:ring-terra-600/30"
          >
            {availableSorts.map((s) => (
              <option key={s} value={s}>
                {t(`countries.sort.${s}`)}
              </option>
            ))}
          </select>
        </label>

        <div
          className="ml-auto inline-flex overflow-hidden rounded-md border border-ink-100"
          role="radiogroup"
          aria-label={t('countries.view.label')}
        >
          {VIEW_KEYS.map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={view === v}
              onClick={() => onViewChange(v)}
              data-testid={`view-${v}`}
              className={cn(
                'px-3 py-1 font-mono text-[11px] uppercase tracking-wider transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terra-600',
                view === v
                  ? 'bg-ink-900 text-paper'
                  : 'bg-paper text-ink-500 hover:bg-paper-2',
              )}
            >
              {t(`countries.view.${v}`)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  Excluded section (collapsed)                                       */
/* ────────────────────────────────────────────────────────────────── */

interface ExcludedSectionProps {
  entries: CountryEntry[];
  lang: 'en' | 'ru';
}

function ExcludedSection({ entries, lang }: ExcludedSectionProps) {
  const { t } = useTranslation();
  if (entries.length === 0) return null;

  return (
    <details className="mt-10 rounded-lg border border-ink-100 bg-paper-2/60" data-testid="excluded-section">
      <summary className="flex cursor-pointer items-baseline justify-between gap-4 rounded-t-lg px-5 py-4 hover:bg-paper-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terra-600">
        <div>
          <p className="font-mono text-[11px] font-medium uppercase tracking-wider text-ink-500">
            {t('countries.excludedSection.title', { count: entries.length })}
          </p>
          <p className="mt-1 text-sm text-ink-500">
            {t('countries.excludedSection.subtitle')}
          </p>
        </div>
        <span aria-hidden="true" className="font-mono text-ink-400">
          ▾
        </span>
      </summary>
      <div className="grid grid-cols-1 gap-4 border-t border-ink-100 p-5 sm:grid-cols-2 lg:grid-cols-3">
        {entries.map((e) => (
          <ExcludedCountryCard
            key={e.country.code}
            country={e.country}
            match={e.match!}
            lang={lang}
          />
        ))}
      </div>
    </details>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/*  Page                                                               */
/* ────────────────────────────────────────────────────────────────── */

function isRegionKey(v: string | null): v is RegionKey {
  return !!v && REGION_FILTERS.some((f) => f.key === v);
}

function isSortKey(v: string | null): v is SortKey {
  return !!v && ALL_SORT_KEYS.includes(v as SortKey);
}

function isVisaBucket(v: string | null): v is VisaBucket {
  return !!v && (VISA_BUCKETS as readonly string[]).includes(v);
}

function isViewKey(v: string | null): v is ViewKey {
  return !!v && (VIEW_KEYS as readonly string[]).includes(v);
}

const VIEW_STORAGE_KEY = 'rm:countries:view';
const LIST_MONEY = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export default function CountriesPage() {
  useDocumentLang();
  const { t } = useTranslation();
  // Legacy URL forwarding: /countries#XX (AI-helper linkify, old shared links)
  // now redirects to the country hub /countries/<slug>.  Hash-only routes are
  // not crawlable; the new hub URLs are.  See ROADMAP §3.
  const location = useLocation();
  // The URL — not i18n state — decides which language this page IS.  The two
  // agree once LangUrlSync has run, but the canonical must be right on the
  // very first render: that is the one the prerenderer snapshots.
  const lang: 'en' | 'ru' = stripLangPrefix(location.pathname).lang === 'ru' ? 'ru' : 'en';
  const navigate = useNavigate();
  useEffect(() => {
    const forwardHash = () => {
      const hash = window.location.hash.replace(/^#/, '');
      if (!hash || !/^[a-z]{2,3}$/i.test(hash)) return;
      const dest = buildWikiUrl({ cc: hash.toUpperCase() as WikiCountryCode, lang });
      if (dest && dest !== window.location.pathname) {
        navigate(dest, { replace: true });
      }
    };
    forwardHash();
    window.addEventListener('hashchange', forwardHash);
    return () => window.removeEventListener('hashchange', forwardHash);
  }, [location.hash, lang, navigate]);
  // The atlas root is the one bilingual surface that had a canonical but no
  // alternates: /countries and /ru/countries looked like two unrelated pages
  // to Google, which keeps only one of a duplicate pair.  buildWikiUrl() with
  // no `cc` yields exactly the atlas-root URLs build-sitemap.mjs advertises.
  const atlasUrl = buildWikiUrl({ lang, origin: PROD_ORIGIN });
  useSeoMeta({
    title: t('countries.meta.title'),
    description: t('countries.meta.description'),
    canonical: atlasUrl,
    locale: lang === 'ru' ? 'ru_RU' : 'en_US',
  });
  useWikiHreflang({});
  useJsonLd(
    JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: t('app.title'),
          item: `${PROD_ORIGIN}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: t('countries.title'),
          item: atlasUrl,
        },
      ],
    }),
    'countries-breadcrumb',
  );
  const { data: countriesFile, isLoading } = useQuery(countriesQuery());
  const result = useQuizStore((s) => s.result);

  const [searchParams, setSearchParams] = useSearchParams();
  const regionParam = searchParams.get('region');
  const sortParam = searchParams.get('sort');
  const visaParam = searchParams.get('visa');
  const viewParam = searchParams.get('view');

  const region: RegionKey = isRegionKey(regionParam) ? regionParam : 'all';
  const visaBucket: VisaBucket = isVisaBucket(visaParam) ? visaParam : 'any';

  const availableSorts: readonly SortKey[] = result
    ? ALL_SORT_KEYS
    : ALL_SORT_KEYS.filter((s) => s !== 'match');

  const defaultSort: SortKey = result ? 'match' : 'tier';
  const sortKey: SortKey = isSortKey(sortParam) && availableSorts.includes(sortParam)
    ? sortParam
    : defaultSort;

  // View persists in localStorage as a UI preference; URL takes precedence
  // for shared links.
  const storedView = (() => {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(VIEW_STORAGE_KEY);
    } catch {
      return null;
    }
  })();
  const view: ViewKey = isViewKey(viewParam)
    ? viewParam
    : isViewKey(storedView)
      ? storedView
      : 'grid';

  const setRegion = (r: RegionKey) => {
    const next = new URLSearchParams(searchParams);
    if (r === 'all') next.delete('region');
    else next.set('region', r);
    setSearchParams(next, { replace: true });
  };

  const setSort = (s: SortKey) => {
    const next = new URLSearchParams(searchParams);
    if (s === defaultSort) next.delete('sort');
    else next.set('sort', s);
    setSearchParams(next, { replace: true });
  };

  const setVisa = (v: VisaBucket) => {
    const next = new URLSearchParams(searchParams);
    if (v === 'any') next.delete('visa');
    else next.set('visa', v);
    setSearchParams(next, { replace: true });
  };

  const setView = (v: ViewKey) => {
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, v);
    } catch {
      // storage blocked — ignore
    }
    const next = new URLSearchParams(searchParams);
    if (v === 'grid') next.delete('view');
    else next.set('view', v);
    setSearchParams(next, { replace: true });
  };

  const allEntries = useMemo<CountryEntry[]>(() => {
    if (!countriesFile) return [];
    return buildEntries(
      countriesFile.countries,
      result?.topMatches,
      result?.excluded,
      result?.unscored,
    );
  }, [countriesFile, result]);

  const byCode = useMemo<Map<string, Country>>(
    () => new Map((countriesFile?.countries ?? []).map((c) => [c.code, c])),
    [countriesFile],
  );

  const regionFilter = REGION_FILTERS.find((f) => f.key === region) ?? REGION_FILTERS[0];
  const filtered = useMemo(
    () =>
      allEntries.filter(
        (e) =>
          regionFilter.test(e.country) &&
          matchesVisaBucket(e.country.visa_programs, visaBucket),
      ),
    [allEntries, regionFilter, visaBucket],
  );

  const { data: costFile } = useCostOfLiving();
  const sorted = useMemo(
    () => sortEntries(filtered, sortKey, lang, costFile),
    [filtered, sortKey, lang, costFile],
  );

  // For grid: hide excluded (they live in their own collapsible section).
  const gridEntries = sorted.filter((e) => !e.excluded);
  const excludedEntries = sorted.filter((e) => e.excluded);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:py-14">
      <header className="mb-8">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h1 className="font-display text-3xl font-medium tracking-tight text-ink-900 sm:text-[36px]">
            {t('countries.title')}
          </h1>
          {result ? (
            <Link
              to="/quiz/result"
              className="font-mono text-xs uppercase tracking-wider text-terra-600 hover:underline"
            >
              {t('countries.backToResult')}
            </Link>
          ) : (
            <Link
              to="/quiz"
              className="font-mono text-xs uppercase tracking-wider text-terra-600 hover:underline"
            >
              {t('countries.startQuiz')}
            </Link>
          )}
        </div>
        <p className="mt-2 text-sm text-ink-500">
          {result ? t('countries.subtitlePersonal') : t('countries.subtitle')}
        </p>
      </header>

      {result && (
        <div className="mb-6 flex items-center gap-3 rounded-lg border border-ink-100 bg-paper-2 px-4 py-3">
          <span aria-hidden="true" className="shrink-0 font-mono text-xs text-terra-600">
            ✦
          </span>
          <p className="text-sm text-ink-700">{t('countries.personalBanner')}</p>
        </div>
      )}

      <Link
        to={buildGuideUrl({ lang })}
        className="mb-6 flex items-center justify-between gap-3 rounded-lg border border-ink-100 bg-paper-2/60 px-4 py-3 transition-colors hover:bg-paper-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terra-600"
      >
        <span className="flex items-center gap-3">
          <span aria-hidden="true" className="font-mono text-xs text-terra-600">◇</span>
          <span>
            <span className="block font-display text-sm font-medium italic text-ink-900 sm:text-base">
              {t('countries.guidePromo.title', { defaultValue: 'New here? Start with the Field Guide' })}
            </span>
            <span className="block text-xs text-ink-500 sm:text-sm">
              {t('countries.guidePromo.subtitle', {
                defaultValue: 'How to use the map, pins, shortlist and compare table.',
              })}
            </span>
          </span>
        </span>
        <span aria-hidden="true" className="shrink-0 font-mono text-xs text-ink-400">→</span>
      </Link>

      {result && result.topMatches.length > 0 && (
        <TopThreeHighlight
          matches={result.topMatches}
          byCode={byCode}
          lang={lang}
          total={result.topMatches.length + result.excluded.length + result.unscored.length}
        />
      )}

      <ControlsBar
        region={region}
        onRegionChange={setRegion}
        sortKey={sortKey}
        onSortChange={setSort}
        availableSorts={availableSorts}
        visaBucket={visaBucket}
        onVisaChange={setVisa}
        view={view}
        onViewChange={setView}
      />

      <p
        className="mb-5 font-mono text-[11px] leading-relaxed text-ink-500"
        data-testid="data-note"
      >
        {t('countries.metrics.dataNote')}
      </p>

      {isLoading ? (
        view === 'grid' ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <CountryCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="animate-pulse rounded-lg border border-ink-100 bg-paper-2 p-5">
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-6 w-full rounded bg-ink-100" />
              ))}
            </div>
          </div>
        )
      ) : gridEntries.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-ink-400">
          <span className="font-mono text-sm">{t('countries.noResults')}</span>
        </div>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {gridEntries.map((e) => {
            if (e.match && !e.excluded) {
              return (
                <CountryScoreCard
                  key={e.country.code}
                  match={e.match}
                  country={e.country}
                  lang={lang}
                  weights={result?.weights ?? DEFAULT_WEIGHTS}
                  t={t}
                />
              );
            }
            return (
              <NeutralCountryCard
                key={e.country.code}
                country={e.country}
                lang={lang}
              />
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border border-ink-100 bg-paper-2 px-5 sm:px-6">
          <div className="grid grid-cols-[28px_28px_1fr_64px_64px_72px_88px_88px] items-baseline gap-3 border-b border-ink-100 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-400 sm:gap-4">
            <span aria-hidden="true" />
            <span>Code</span>
            <span>Name · Region</span>
            <span>Tier</span>
            <Tooltip
              content={`${t('countries.metrics.costHousehold.single')}. ${t(
                'countries.metrics.costHelper',
              )} ${t('quiz.cost.disclaimer')}`}
            >
              <span className="cursor-help text-right">
                {t('countries.metrics.costLabel')}
              </span>
            </Tooltip>
            <Tooltip content={t('countries.metrics.taxHelper')}>
              <span className="cursor-help text-right">
                {t('countries.metrics.taxLabel')}
              </span>
            </Tooltip>
            <span className="text-right">{result ? 'Match' : 'Action'}</span>
            <span className="text-right">{t('countries.checklistColLabel', { defaultValue: 'Checklist' })}</span>
          </div>
          <ul>
            {gridEntries.map((e) => (
              <CountryListRow
                key={e.country.code}
                country={e.country}
                match={e.match}
                excluded={e.excluded}
                lang={lang}
              />
            ))}
          </ul>
        </div>
      )}

      {excludedEntries.length > 0 && (
        <ExcludedSection entries={excludedEntries} lang={lang} />
      )}

      {countriesFile && (
        <ShippingStrip countries={countriesFile.countries} lang={lang} />
      )}

      <div className="mt-12 border-t border-ink-100 pt-6">
        {result ? (
          <div className="flex flex-wrap gap-3">
            <Link
              to="/quiz/result"
              className="inline-flex items-center gap-2 rounded-md bg-terra-600 px-5 py-2.5 font-sans text-sm font-medium text-paper transition-colors hover:bg-terra-700"
            >
              {t('countries.backToResult')}
            </Link>
            <Link
              to="/quiz"
              className="inline-flex items-center rounded-md border border-ink-100 px-5 py-2.5 font-sans text-sm font-medium text-ink-700 transition-colors hover:bg-paper-2"
            >
              {t('quiz.result.retake')}
            </Link>
          </div>
        ) : (
          <Link
            to="/quiz"
            className="inline-flex items-center gap-2 rounded-md bg-terra-600 px-5 py-2.5 font-sans text-sm font-medium text-paper transition-colors hover:bg-terra-700"
          >
            {t('countries.startQuiz')}
          </Link>
        )}
      </div>
    </div>
  );
}
